import { ChangeDetectorRef, Component, ElementRef, OnInit } from '@angular/core';
import { CvaLoggerService } from './services/cva-logger/cva-logger.service';
import { ActivatedRoute, Router } from '@angular/router';
import { API_ROUTES, ROUTE_CONSTANTS } from './util/route.constants';
import { LoginService } from './services/login/login.service';
import { LicServiceService } from './services/lic/lic-service.service';
import { CVANotificationService } from './services/cva-notification/cva-notification.service';
import { CVA_CONSTANTS, StorageKeys } from './util/constants';
import { hasAuthTokens } from './util/auth-tokens.util';
import { AppointmentService } from './services/appointment/appointment.service';
import { SessionInactivityService } from './services/session-inactivity/session-inactivity.service';
import { ReAuthPopupService } from './components/auth/reauth-popup/services/reauth-popup.service';
import { environment } from '../environments/environment';
import { GlobalHttpLoaderComponent } from './shared/global-http-loader/global-http-loader.component';

@Component({
  standalone: false,
  selector: 'cva-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',

})
export class AppComponent implements OnInit {
  globalHttpLoaderComponent = GlobalHttpLoaderComponent;
  loggedIn = false;
  isLIC = false;
  licObject: {
    practitionerId: string;
    appointmentId: string;
    fltValue: string;
    token: string;
    facility: string;
  } = { practitionerId: '', appointmentId: '', fltValue: '', token: '', facility: '' };
  LOADER_EXCLUSION_ENDPOINTS = [
    // AI Proxy endpoints
    environment.cva_url + API_ROUTES.AI_PROXY + API_ROUTES.GENERATE_TASKS,
    environment.cva_url + API_ROUTES.AI_PROXY + API_ROUTES.GENERATE_CLASSIFICATION,
    environment.cva_url + API_ROUTES.AI_PROXY + API_ROUTES.GENERATE_MEDICATIONS,
    environment.cva_url + API_ROUTES.AI_PROXY + API_ROUTES.GENERATE_CDM_PLAN,
    environment.cva_url + API_ROUTES.AI_PROXY + API_ROUTES.GENERATE_ACC_ELIGIBILITY,
    // Historical data endpoints - exclude from global loader to show per-card skeleton loaders
    environment.cva_url + API_ROUTES.ALLERGIES_BY_PATIENT_ID,
    environment.cva_url + API_ROUTES.IMMUNIZATION_BY_PATIENT_ID,
    environment.cva_url + API_ROUTES.PREVIOUS_CONSULTATION_BY_PATIENT_ID,
    environment.cva_url + API_ROUTES.VITALS_BY_PATIENT_ID,
    environment.cva_url + API_ROUTES.PATIENT,
    environment.cva_url + API_ROUTES.APPOINTMENT,
    environment.cva_url + API_ROUTES.PRACTITIONER,
    environment.cva_url + API_ROUTES.MEDICATION_REQUEST_BY_PATIENT_ID,
    environment.cva_url + API_ROUTES.LAB_RESULTS,
    environment.cva_url + '/soap-note',
    environment.cva_url + API_ROUTES.AI_PROXY + API_ROUTES.GENERATE_VITALS,
    environment.cva_url + API_ROUTES.STT_AUDIO_GATEWAY
  ];

  constructor(
    public cvaLogger: CvaLoggerService,
    public cvaNotificationService: CVANotificationService,
    public router: Router,
    public loginService: LoginService,
    public appointmentService: AppointmentService,
    public cdr: ChangeDetectorRef,
    public licService: LicServiceService,
    private readonly _elementRef: ElementRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly sessionInactivityService: SessionInactivityService,
    private readonly reauthPopupService: ReAuthPopupService
  ) {
    this.activatedRoute.fragment.subscribe(fragment => {
      const querySet = Object.fromEntries(fragment?.split('&')?.map(e => e.split("=")) || []);
      if (querySet?.facilityCode) {
        localStorage.setItem(StorageKeys.FACILITY_CODE, querySet.facilityCode);
      }
      if (querySet?.redirectTo) {
        localStorage.setItem('redirectTo', querySet.redirectTo);
      }
    });
  }

  ngOnInit() {
    this.intialCheck()
  }

  private async intialCheck() {
    this._elementRef.nativeElement.removeAttribute("ng-version");
    this.isLIC = this.checkAndExtract(window.location.search);

    if (this.isLIC) {
      localStorage.setItem('isLIC', this.isLIC.toString());
      const licObject = this.licObject;
      this.router.navigate([ROUTE_CONSTANTS.LIC], {
        state: { licObject },
      });
    } else {
      this.loginService.loggedIn$.subscribe(async (val: boolean) => {
        this.loggedIn = val;
        this.cdr.detectChanges();
        if (this.loggedIn && hasAuthTokens()) {
          this.sessionInactivityService.initOnLogin()
            .then(() => console.log("Started tracking session inactivity...")
            ).catch((err) => {
              console.error("Session inactivity issue:\n", err);
            });
        }
      });
    }
    this.isLIC = localStorage.getItem('isLIC') === 'true';
    if (!this.isLIC) {
      this.sessionInactivityService.onInactive(() => this.reauthPopupService.onReAuth());
    }
  }

  isLoginPage(): boolean {
    return this.router.url === '/' + ROUTE_CONSTANTS.LOGIN;
  }

  shouldShowGlobalLoader(): boolean {
    const routePath = this.router.url.split('?')[0].split('#')[0];

    const isDashboardLikeRoute =
      routePath.startsWith('/' + ROUTE_CONSTANTS.PATIENT_DASHBOARD)
    return !isDashboardLikeRoute
  }

  extractValue(pattern: RegExp, str: string): string | null {
    const match = pattern.exec(str);
    return match ? match[1] : null;
  }

  checkAndExtract(inputString: string): boolean {
    if (!inputString) {
      return false;
    }

    const extractedValues = CVA_CONSTANTS.REGEX.patterns.map((pattern) =>
      this.extractValue(pattern, inputString)
    );
    const [practitionerId, appointmentId, fltValue, token, facility] = extractedValues;

    if (fltValue && practitionerId && token && appointmentId && facility) {
      this.licObject.fltValue = fltValue;
      this.licObject.practitionerId = practitionerId;
      this.licObject.token = token;
      this.licObject.appointmentId = appointmentId;
      this.licObject.facility = facility;
      return true;
    }
    return false;
  }
}
